import React, { useEffect, useState } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { tooltipFormatterList } from 'helpers/echart-utils';
import { fetchHVACampaignTodayFunnel } from 'services/hvacallingmanagment.service';
import { fetchHVACampaignTodayFunnelInbound } from 'services/hvacallingmanagment.service';

// Inicializar componentes de ECharts
echarts.use([TooltipComponent, BarChart]);

interface CampaignFunnelChartSmallProps {
  campaignid: number;
  campaignType?: 'outbound' | 'inbound';
}

// Definimos el tipo de datos que esperamos recibir
interface OutboundFunnelData {
  clients: number;
  dialed: number;
  answer: number;
  rpc: number;
  positive: number;
}

interface InboundFunnelData {
  offered: number;
  transferred: number;
  handled: number;
  positive: number;
}

type CampaignFunnelData = OutboundFunnelData | InboundFunnelData;

// Función que devuelve las opciones por defecto del gráfico
const getDefaultOptions = (
  getThemeColor: (name: string) => string,
  addAlphaToHexColor: (hex: string, alpha: number) => string,
  isDark: boolean,
  data: number[],
  baseValue: number,
  campaignType: 'outbound' | 'inbound'
) => {
  const styles =
    campaignType === 'inbound'
      ? [
          {
            // eslint-disable-next-line
            color: !isDark ? getThemeColor('success-light') : getThemeColor('success-light'),
            // eslint-disable-next-line
            emphasisColor: !isDark ? getThemeColor('success') : getThemeColor('success-dark')
          },
          {
            // eslint-disable-next-line
            color: !isDark ? getThemeColor('primary-lighter') : getThemeColor('primary-light'),
            emphasisColor: getThemeColor('secondary')
          },
          {
            // eslint-disable-next-line
            color: !isDark ? getThemeColor('primary-light') : addAlphaToHexColor(getThemeColor('primary-light'),0.8),
            emphasisColor: getThemeColor('secondary')
          },
          {
            // eslint-disable-next-line
            color: !isDark ? getThemeColor('primary') : addAlphaToHexColor(getThemeColor('primary-light'),0.6),
            emphasisColor: getThemeColor('secondary')
          }
        ]
      : [
          {
            // eslint-disable-next-line
            color: !isDark ? getThemeColor('success-light') : getThemeColor('success-light'),
            // eslint-disable-next-line
            emphasisColor: !isDark ? getThemeColor('success') : getThemeColor('success-dark')
          },
          {
            // eslint-disable-next-line
            color: !isDark ? getThemeColor('primary-lighter') : getThemeColor('primary-light'),
            emphasisColor: getThemeColor('secondary')
          },
          {
            // eslint-disable-next-line
            color: !isDark ? getThemeColor('primary-light') : addAlphaToHexColor(getThemeColor('primary-light'),0.8),
            emphasisColor: getThemeColor('secondary')
          },
          {
            // eslint-disable-next-line
            color: !isDark ? getThemeColor('primary') : addAlphaToHexColor(getThemeColor('primary-light'),0.6),
            emphasisColor: getThemeColor('secondary')
          },
          {
            // eslint-disable-next-line
            color: !isDark ? getThemeColor('primary-darker') : addAlphaToHexColor(getThemeColor('primary-light'),0.2),
            emphasisColor: getThemeColor('secondary')
          }
        ];

  // Actualizar yAxis data
  const yAxisData =
    campaignType === 'inbound'
      ? ['Positive', 'Handled', 'Transferred', 'Offered']
      : ['Positive', 'RPC', 'Answer', 'Dial', 'Clients'];

  return {
    color: styles.map(style => style.color),
    tooltip: {
      trigger: 'axis',
      padding: [7, 10],
      // eslint-disable-next-line
      backgroundColor: !isDark ? 'rgba(240, 240, 240, 0.9)' : 'rgba(49, 55, 74, 0.9)',
      textStyle: {
        color: !isDark ? '#202020' : '#ccc',
        fontFamily: 'Nunito Sans',
        fontSize: 14
      },
      borderColor: getThemeColor('border-color'),
      borderWidth: 1,
      transitionDuration: 0,
      axisPointer: {
        type: 'none'
      },
      formatter: tooltipFormatterList
    },
    xAxis: {
      type: 'value',
      inverse: false,
      axisLabel: { show: false },
      show: false,
      axisLine: { lineStyle: { color: getThemeColor('tertiary-bg') } },
      axisTick: false
    },
    yAxis: {
      data: yAxisData,
      type: 'category',
      axisPointer: { type: 'none' },
      axisTick: 'none',
      splitLine: {
        interval: 1,
        lineStyle: { color: getThemeColor('secondary-bg') }
      },
      axisLine: { show: false },
      axisLabel: {
        show: true,
        align: 'left',
        margin: 80,
        color: getThemeColor('body-color')
      }
    },
    series: [
      {
        name: 'Customer conversion',
        type: 'bar',
        barWidth: '16px',
        showBackground: false,
        backgroundStyle: { borderRadius: [4, 4, 4, 4] },
        data: data.map((value, index) => ({
          value,
          itemStyle: {
            color: styles[index].color,
            borderRadius: [4, 4, 4, 4]
          },
          emphasis: {
            itemStyle: {
              color: styles[index].emphasisColor
            }
          },
          label: {
            show: true,
            position: 'insideTop',
            distance: 3,
            formatter:
              baseValue > 0
                ? `${Math.round((value / baseValue) * 100)}%`
                : '0%',
            fontWeight: 600,
            fontSize: 12
          }
        }))
      }
    ],
    grid: {
      right: 0,
      left: 80,
      bottom: 0,
      top: 0,
      containLabel: false
    },
    animation: true
  };
};

const CampaignFunnelChartSmall: React.FC<CampaignFunnelChartSmallProps> = ({
  campaignid,
  campaignType = 'outbound'
}) => {
  // eslint-disable-next-line
  const { config: { isDark }, getThemeColor, addAlphaToHexColor } = useAppContext();
  const [chartData, setChartData] = useState<number[]>([]);
  const [baseValue, setBaseValue] = useState<number>(0);

  // Función para obtener los datos del funnel
  const fetchData = async () => {
    try {
      let data: CampaignFunnelData;

      if (campaignType === 'inbound') {
        // Necesitarás crear este endpoint
        data = await fetchHVACampaignTodayFunnelInbound(campaignid);
        const inboundData = data as InboundFunnelData;

        setBaseValue(inboundData.offered); // Base para porcentajes
        setChartData([
          inboundData.positive,
          inboundData.handled,
          inboundData.transferred,
          inboundData.offered
        ]);
      } else {
        data = await fetchHVACampaignTodayFunnel(campaignid);
        const outboundData = data as OutboundFunnelData;

        setBaseValue(outboundData.clients); // Base para porcentajes
        setChartData([
          outboundData.positive,
          outboundData.rpc,
          outboundData.answer,
          outboundData.dialed,
          outboundData.clients
        ]);
      }
    } catch (error) {
      console.error('Error fetching campaign data:', error);
    }
  };

  useEffect(() => {
    // Llamar a fetchData inicialmente y configurar el intervalo de actualización
    fetchData();
    const intervalId = setInterval(fetchData, 10000); // Actualizar cada 10 segundos

    // Limpiar el intervalo cuando el componente se desmonte
    return () => clearInterval(intervalId);
  }, [campaignid, campaignType]);

  return (
    <ReactEChartsCore
      echarts={echarts}
      // eslint-disable-next-line
      option={getDefaultOptions(
        getThemeColor,
        addAlphaToHexColor,
        isDark,
        chartData,
        baseValue,
        campaignType
      )}
      style={{ height: '90px', width: '95%' }}
    />
  );
};

export default CampaignFunnelChartSmall;
